import React, { useEffect, useState } from 'react';
import { ArrowRight, Play, ShieldCheck, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { HERO_IMAGE, LOGO_URL, STATS, COMPANY } from '../mock';

export default function Hero() {
  const navigate = useNavigate();
  const [tx, setTx] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const onMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 14;
      const y = (e.clientY / window.innerHeight - 0.5) * 10;
      setTx({ x, y });
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <section className="relative overflow-hidden bg-white">
      <div className="absolute -top-20 -right-20 w-[440px] h-[440px] rounded-full bg-[#e11d2c]/8 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 -left-32 w-[360px] h-[360px] rounded-full bg-black/5 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-14 lg:pt-20 pb-20 lg:pb-28 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-6 relative z-10">
          <div className="inline-flex items-center gap-2 bg-black text-white px-3.5 py-1.5 rounded-full text-[11px] tracking-[0.18em] uppercase font-semibold">
            <span className="pulse-dot" /> Open Mon–Sat · 9 to 5
          </div>

          <h1 className="font-display mt-6 text-[44px] sm:text-[58px] lg:text-[72px] leading-[0.98] tracking-tight text-black font-black">
            Don’t just <br className="hidden sm:block" />
            <span className="kinetic-line text-[#e11d2c]">dream</span> it. <span className="italic">Drive it.</span>
          </h1>

          <p className="mt-6 text-[16px] sm:text-[17px] leading-relaxed text-black/65 max-w-xl">
            Hand-picked Japanese imports, full mechanical & hybrid service, body work, parts, and accessories — one location in Four Path, Clarendon.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Button
              onClick={() => navigate('/inventory')}
              className="btn-primary group bg-[#e11d2c] hover:bg-[#c01526] text-white font-semibold tracking-wide rounded-full h-12 px-7 text-[14px]"
            >
              Browse Inventory
              <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button
              onClick={() => navigate('/services')}
              variant="outline"
              className="rounded-full h-12 px-6 border-black/15 hover:border-black hover:bg-black hover:text-white text-[14px] font-semibold"
            >
              <Play size={14} className="mr-2 fill-current" />
              Our Services
            </Button>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-6 text-[13px] text-black/65">
            <div className="inline-flex items-center gap-2">
              <ShieldCheck size={16} className="text-[#e11d2c]" />
              <span><b className="text-black">100%</b> Verified imports</span>
            </div>
            <div className="inline-flex items-center gap-2">
              <Star size={16} className="text-[#e11d2c] fill-[#e11d2c]" />
              <span><b className="text-black">4.9/5</b> customer rating</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-6 relative">
          <div
            className="relative aspect-[5/4] sm:aspect-[6/4] rounded-[28px] overflow-hidden bg-black"
            style={{ transform: `translate3d(${tx.x * -0.5}px, ${tx.y * -0.4}px, 0)`, transition: 'transform 300ms ease-out' }}
          >
            <img src={HERO_IMAGE} alt="Timeless Auto Imports" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/55 via-black/15 to-transparent" />

            <div className="absolute left-5 sm:left-6 bottom-5 sm:bottom-6 right-5 sm:right-auto sm:w-[300px] bg-white/95 backdrop-blur rounded-2xl p-5 shadow-2xl border border-white">
              <div className="flex items-center gap-3">
                <img src={LOGO_URL} alt="" className="w-12 h-12 rounded-lg object-cover" />
                <div className="min-w-0">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#e11d2c] font-bold">One Stop</div>
                  <div className="font-display text-[18px] font-bold text-black leading-tight">Complete Automotive Solution</div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-black/10 flex items-center justify-between text-[11px]">
                <div className="text-black/55">Call now</div>
                <a href={`tel:${COMPANY.phone}`} className="font-bold text-black">{COMPANY.phone}</a>
              </div>
            </div>

            <div
              className="absolute top-5 right-5 bg-black text-white rounded-full px-4 py-2 text-[11px] tracking-[0.18em] uppercase font-semibold shadow-xl"
              style={{ transform: `translate3d(${tx.x * 0.8}px, ${tx.y * 0.8}px, 0)`, transition: 'transform 300ms ease-out' }}
            >
              <span className="text-[#e11d2c]">●</span> Cars in stock
            </div>
          </div>

          <div className="mt-6 grid grid-cols-4 gap-3 sm:gap-4">
            {STATS.map((s, i) => (
              <div key={i} className="bg-white border border-black/10 rounded-xl px-3 py-4 text-center">
                <div className="font-display text-[20px] sm:text-[26px] font-bold text-black leading-none">{s.value}</div>
                <div className="text-[10px] sm:text-[11px] tracking-wider uppercase text-black/55 mt-1.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border-y border-black/10 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex items-center gap-6 overflow-hidden">
          <span className="shrink-0 text-[11px] tracking-[0.22em] uppercase font-semibold text-black/55">Brands We Service</span>
          <div className="flex-1 overflow-hidden">
            <div className="flex gap-12 marquee-track whitespace-nowrap">
              {[...Array(2)].map((_, k) => (
                <div key={k} className="flex gap-12">
                  {['TOYOTA','HONDA','SUBARU','NISSAN','MERCEDES-BENZ','BMW','DAIHATSU','LEXUS','MAZDA','SUZUKI'].map((b, i) => (
                    <span key={i} className="font-display text-[20px] sm:text-[22px] font-bold tracking-[0.15em] text-black/80 hover:text-[#e11d2c] transition-colors">
                      {b}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
